import { AlertTriangle, CheckCircle2, Volume1 } from 'lucide-react';
import type { MeetlyAudioReport as Report } from '../api/meetly';

/**
 * The "was it heard?" note on a finished meeting. One line for a clean
 * recording; a counted breakdown and what to do next when parts were silent,
 * quiet or lost — so a thin summary is explained, not just presented.
 */
export function audioAdvice(report: Report): string | null {
  if (report.level === 'good') return null;
  if (report.silent + report.quiet >= Math.max(1, report.failed)) {
    return 'Next time, bring the microphone closer to the people speaking, or use the mic check before starting.';
  }
  return 'Parts could not be transcribed; if the audio was fine, Regenerate summary will retry the summary only.';
}

export default function MeetlyAudioReport({ report }: { report: Report }) {
  const Icon = report.level === 'good' ? CheckCircle2 : report.level === 'poor' ? AlertTriangle : Volume1;
  const tone = report.level === 'good'
    ? 'border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] text-[var(--gt-text-secondary)]'
    : report.level === 'poor'
      ? 'border-red-200 bg-red-50 text-red-800'
      : 'border-[#f3d9a4] bg-[#fff7e6] text-[#7a4b12]';
  const stats: string[] = [`${report.transcribed} of ${report.parts} parts heard`];
  if (report.silent) stats.push(`${report.silent} silent`);
  if (report.quiet) stats.push(`${report.quiet} quiet`);
  if (report.failed) stats.push(`${report.failed} failed`);
  if (report.mean_db !== null) stats.push(`avg ${Math.round(report.mean_db)} dB`);
  const advice = audioAdvice(report);

  return (
    <div className={`flex gap-3 rounded-2xl border px-4 py-3 text-sm ${tone}`} role="note" aria-label="Audio quality" data-testid="meetly-audio-report">
      <Icon size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="font-medium">{report.verdict}</p>
        <p className="mt-0.5 text-xs opacity-80">{stats.join(' · ')}</p>
        {advice && <p className="mt-1 text-xs leading-5">{advice}</p>}
      </div>
    </div>
  );
}
